import { useSelector } from "react-redux";
import useLabels from "../../hooks/useLabels";
import {
  selectActiveWinery,
  selectConfigs,
  selectUser,
} from "../../context/selectors";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CTA, Dropdown, Modal } from "../Generic";

import "./styles/index.scss";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { WineriesSelector } from "../WineriesList";
// import NextOperations from "./NextOperations";
import useAuth from "../../hooks/useAuth";
import { ROLES } from "../../constants/base";
// import AnalysisGrid from "./AnalysisGrid";
import { addMonths, format } from "date-fns";
import { getPDF } from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { getItem } from "../../services/LocalStorage";
import { isRedirectFromValidForWinery } from "../../services/redirect";
import { SkeletonAnalysisGrid, SkeletonNextOperations } from "../Skeleton/Skeleton";
import { Icon } from "../../components/Icon";

const AnalysisGrid = lazy(() => import("./AnalysisGrid"));
const NextOperations = lazy(() => import("./NextOperations"));

const Home = ({ isMobile }) => {
  const [getLabel] = useLabels();
  const configs = useSelector(selectConfigs);
  const user = useSelector(selectUser);
  const ref = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const activeWinery = useSelector(selectActiveWinery);
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const axiosPrivate = useAxiosPrivate();
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (location.hash.includes("wineriesList") && !ref?.current?.open) {
      ref?.current?.showModal();
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!location.hash.includes("wineriesList") && ref?.current?.open) {
      ref?.current?.close();
      setIsOpen(false);
    }

    if (activeWinery === null) {
      ref?.current?.close();
      ref?.current?.showModal();
      setIsOpen(true);
    }

  }, [location.hash]);

  const winerySelectionCallback = (selectedWinery) => {
    // "from" arriva da Login (query token scaduto/logout): torna a quella pagina
    // solo se appartiene alla winery appena selezionata, altrimenti resta in Home
    // (l'URL potrebbe riferirsi a un tank/lotto/operazione di un'altra cantina).
    const from = location.state?.from;
    navigate(
      from && isRedirectFromValidForWinery(from, selectedWinery?.id)
        ? from.pathname
        : location.pathname
    );
    if (ref?.current?.open) {
      ref?.current?.close();
      setIsOpen(false);
    }
  };

  const downloadDailyReport = async () => {
    const data = {
      pdf_type: "dailyReport",
      type: "operations",
      title: `${getLabel("printDailyExecutedOperation")} - ${new Date().toLocaleDateString("it-IT")}`,
      rows: [activeWinery?.id],
      columns: [
        getLabel("operationType"),
        getLabel("operationSrcTank"),
        getLabel("operationDestTank"),
        getLabel("operationCompletionUser"),
        getLabel("operationCompetionDate"),
      ],
      headers: [
        "type",
        "src_tanks",
        "dest_tanks",
        "completed_by",
        "completion_date",
      ],
      winery_name: activeWinery?.name || "",
      order_by: "expire_date",
      order_sort: "desc",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language:
        navigator.languages?.length > 0
          ? navigator.languages[0]?.replace("_", "-")
          : "it-IT",
    };

    await getPDF(
      data,
      axiosPrivate,
      getLabel("pdfType_DAILY_REPORT")
    );
  };

  const renderWelcome = () => {
    return (
      <div className="primary_container_upper_info">
        <h2>{user?.type !== 'TANK' ? getLabel("welcome", { username: user?.userName }) : getItem("currentMonitor") ? `${getLabel(user?.type)} ${getItem("currentMonitor")}` : ""}</h2>
        <h3>{getLabel("winery", { activeWinery: activeWinery?.name })}</h3>
        {role === ROLES.CLIENT && (<>
          <div className="home_actions">
            <Dropdown
              label={<>{getLabel("programOperation")}
              {" "}
              <Icon icon="fa-calendar-plus" /></>}
              customClassName={'new_operation'}
              options={[
                {
                  label: getLabel("operationTypeDESTEMMING"),
                  handler: () => navigate("/programming/destemming/new"),
                },
                {
                  label: getLabel("operationTypeCUT"),
                  handler: () => navigate("/programming/cut/new"),
                },
                {
                  label: getLabel("operationTypeRACKING"),
                  handler: () => navigate("/programming/racking/new"),
                },
                {
                  label: getLabel("operationTypeFILTERING"),
                  handler: () => navigate("/programming/filtering/new"),
                },
                {
                  label: getLabel("operationTypeOUILLAGE"),
                  handler: () => navigate("/programming/ouillage/new"),
                },
                {
                  label: getLabel("operationTypeTREATMENT"),
                  handler: () => navigate("/programming/treatment/new"),
                },
                {
                  label: getLabel("operationTypePHYSICAL"),
                  handler: () => navigate("/programming/physical/new"),
                },
                {
                  label: getLabel("operationTypeANALYSIS"),
                  handler: () => navigate("/programming/analysis/new"),
                },
                {
                  label: getLabel("operationTypeDECANT"),
                  handler: () => navigate("/programming/decant/new"),
                },
                {
                  label: getLabel("operationTypeWINEMAKING"),
                  handler: () => navigate("/programming/winemaking/new"),
                },
                {
                  label: getLabel("operationTypeWINEMAKINGPOST"),
                  handler: () => navigate("/programming/winemakingpost/new"),
                },
                {
                  label: getLabel("operationTypeBOTTLING"),
                  handler: () => navigate("/programming/bottling/new"),
                },
                {
                  label: getLabel("operationTypeCLEANING"),
                  handler: () => navigate("/programming/cleaning/new"),
                },
              ]}
            ></Dropdown>
            <CTA customClassName={'complete large'} onClick={() => navigate('/lots/new')}>
              {getLabel("createLot")}
              {" "}
              <Icon icon="fa-plus" />
            </CTA>
            <CTA customClassName={'complete large'} onClick={downloadDailyReport}>
              {getLabel("printDailyExecutedOperation")}
              {" "}
              <Icon icon="fa-print" />
            </CTA>
          </div>
          </>
        )}
      </div>
    );
  };

  const renderReminder = () => {
    return (
      <div className="primary_container_upper_info">
        <h4>{getLabel("nextOperations")}</h4>
        <h5>
          {getLabel("dateFrom") +
            " " +
            format(new Date(), configs.shortDateFormat) +
            " " +
            getLabel("dateTo") +
            " " +
            format(addMonths(new Date(), 1), configs.shortDateFormat)}
        </h5>

        <Card customClassName={'light_gray'}>
          <Suspense fallback={<SkeletonNextOperations rowCount={6} />}>
            <NextOperations hoverColor={'light_gray'} />
          </Suspense>
        </Card>
      </div>
    );
  };

  const renderLastAnalysis = () => {
    return (
      <Suspense fallback={<SkeletonAnalysisGrid title={getLabel("lastAnalysis")} cardCount={3} />}>
        <AnalysisGrid />
      </Suspense>
    );
  };

  const renderLegend = () => {
    return <div>
      <h5>{getLabel("legend")}</h5>
      <ul>
        <li><span>{getLabel("operationExpired")}</span><span className="icon">&#10071;</span></li>
        <li><span>{getLabel("operationExpiringExact")}</span><span className="icon"><Icon icon="fa-calendar-day" color="grey" /></span></li>
        <li><span>{getLabel("operationAnalysisToBeSampled")}</span><span className="icon"><Icon icon="fa-eye-dropper" color="grey" /></span></li>
        <li><span>{getLabel("operationAnalysisSampled")}</span><span className="icon"><Icon icon="fa-flask-vial" color="grey" /></span></li>
        <li><span>{getLabel("operationStatusStarted")}</span><span className="icon"><Icon style={{ color: "#3498db" }} icon="fa-spinner" /></span></li>
        <li><span>{getLabel("operationStatusPending")}</span><span className="icon"><Icon icon="fa-clock" color="grey" /></span></li>
      </ul>
    </div>;
  };

  return (
    <section className="primary_container">
      <div className="primary_container_wrapper">
        <div className="primary_container_upper">
          {renderWelcome()}
          {renderReminder()}
        </div>
        <div className="primary_container_lower">{renderLastAnalysis()}</div>
        <div className="primary_container_lower legend">{renderLegend()}</div>
      </div>
      <Modal forwardRef={ref} customClassName={'is_winery_selector'} isOpen={isOpen} modalType={"login"}>
        <h4>{getLabel("wineriesModalSelector")}</h4>
        <WineriesSelector
          eventCallback={winerySelectionCallback}
          isSelector={true}
          isMobile={isMobile}
          isModal={true}
        />
      </Modal>
    </section>
  );
};

export default Home;
